import { useNavigate } from 'react-router-dom';

import { ActionIcon, Button, Tooltip } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconRocket } from '@tabler/icons-react';

import { EscalateReportModal } from '@/components/Modals/EscalateReportModal';
import { ReportEscalationResponse } from '@/types';

interface EscalateReportButtonProps {
  /** Report to escalate. ``reportConfigId`` is optional (the list row doesn't
   * carry it); the modal resolves it from the report when absent. */
  report: { id: string; reportConfigId?: string | null };
  variant?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  /** When true (default), navigates to the newly-created report on success.
   * Escalating to a manually-fulfilled type produces a triage request rather
   * than a report, and never navigates. */
  navigateOnEscalated?: boolean;
  /** Compact table-row form: just the rocket in a tooltip'd ActionIcon
   * (the accessible name stays "Escalate"). */
  iconOnly?: boolean;
}

export function EscalateReportButton({
  report,
  variant = 'light',
  size = 'sm',
  navigateOnEscalated = true,
  iconOnly = false,
}: Readonly<EscalateReportButtonProps>) {
  const [opened, { open, close }] = useDisclosure(false);
  const navigate = useNavigate();

  const handleEscalated = (result: ReportEscalationResponse) => {
    // Only an escalation that produced a report has somewhere to go. A
    // manually-fulfilled type raises a triage request instead, and triage is
    // an admin surface — this button is the client-facing entry point (admins
    // escalate from the report's admin menu), so the toast is the whole story.
    if (navigateOnEscalated && result.escalated_to_report_id) {
      navigate(`/reports/${result.escalated_to_report_id}`);
    }
  };

  return (
    <>
      {iconOnly ? (
        <Tooltip label="Escalate" withArrow>
          <ActionIcon variant={variant} size="md" onClick={open} aria-label="Escalate">
            <IconRocket size={16} />
          </ActionIcon>
        </Tooltip>
      ) : (
        <Button variant={variant} size={size} leftSection={<IconRocket size={16} />} onClick={open}>
          Escalate
        </Button>
      )}
      <EscalateReportModal
        opened={opened}
        onClose={close}
        report={report}
        onEscalated={handleEscalated}
      />
    </>
  );
}
